// src/layouts/useSectionTabs.ts
//
// The shell's second navigation level, resolved once and consumed by three
// components: SectionTabs renders it, CustomAppBar names the active section
// in the breadcrumb, and CustomLayout reserves the strip's height in the
// content column's top inset (the bar is absolutely positioned under the app
// bar, so the layout has to know when it is there — hybrid-3c.md §5.1).
import { useLocation } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import { useHasSurveyProperty, useHasV2Property } from '@/hooks/useV2PropertySelection';
import { useHasDispensers } from '@/hooks/useHasDispensers';
import { findNavLocation, getVisibleNavGroups, type NavTab, type VisibleNavGroup } from './menuStructure';

/** App bar height, matching CustomAppBar's own `height` (the strip hangs right below it). */
export const APP_BAR_HEIGHT = 52;
/** Section bar height on a pointer-sized viewport. */
export const SECTION_TABS_HEIGHT = 40;
/** Minimum touch target below `sm`; the strip is one pixel taller so the
 *  tab itself still reaches it above the strip's own bottom hairline. */
export const TOUCH_TARGET_MIN = 44;

export interface ShellSections {
  /** Every destination the current user may see (the rail's entries). */
  groups: VisibleNavGroup[];
  /** The destination the current path belongs to, if any. */
  group: VisibleNavGroup | null;
  /** The active section within it, if the path resolves to one. */
  tab?: NavTab;
  /** The sections to render — EMPTY when this user sees fewer than two of
   *  them: a bar with a single tab is noise. */
  sections: NavTab[];
  /** Vertical space the bar occupies: 0 when it is not shown. */
  height: number;
}

export const useSectionTabs = (): ShellSections => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasV2Property = useHasV2Property();
  const hasDispensers = useHasDispensers();
  const hasSurveyProperty = useHasSurveyProperty();

  const groups = getVisibleNavGroups(hasV2Property, hasDispensers, hasSurveyProperty);
  const current = findNavLocation(location.pathname, groups);
  const visible = current?.group.tabs ?? [];
  const sections = visible.length > 1 ? visible : [];
  const barHeight = isMobile ? TOUCH_TARGET_MIN + 1 : SECTION_TABS_HEIGHT;

  return {
    groups,
    group: current?.group ?? null,
    tab: current?.tab,
    sections,
    height: sections.length > 0 ? barHeight : 0,
  };
};

export default useSectionTabs;
